import { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { doc, updateDoc } from "firebase/firestore";
import "bootstrap-icons/font/bootstrap-icons.css";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "20px",
    maxWidth: "450px",
    width: "100%",
    maxHeight: "250px",
    overflow: "auto",
    fontFamily: "Inter, sans-serif",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1008,
  },
};

// helper local
function parseCost(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    return parseFloat(value.replace(/[^0-9.-]+/g, "")) || 0;
  }
  return 0;
}

export default function ProductButton({
  isOpen,
  onClose,
  products,
  selectedProducts,
  productosCol,
}) {
  const [percentage, setPercentage] = useState(10);

  // reset opcional al abrir
  useEffect(() => {
    if (isOpen) setPercentage(10);
  }, [isOpen]);

  const handleBulkAdjustPrices = async (increase, percentage) => {
    const pct = Number(percentage);
    if (!pct || pct <= 0) {
      return Swal.fire("Por favor", "Ingresa un porcentaje válido.", "info");
    }

    const idsSeleccionados = Object.keys(selectedProducts || {}).filter(
      (k) => selectedProducts[k]
    );
    const targetList =
      idsSeleccionados.length > 0
        ? products.filter((p) => idsSeleccionados.includes(p.id))
        : products;

    if (!targetList.length) {
      return Swal.fire(
        "Nada por actualizar",
        "No hay productos a ajustar.",
        "info"
      );
    }

    try {
      const factor = 1 + (increase ? pct : -pct) / 100;
      await Promise.all(
        targetList.map((p) => {
          const costoNum = parseCost(p.costo);
          const nuevo = Math.max(0, Number((costoNum * factor).toFixed(2)));
          return updateDoc(doc(productosCol, p.id), { costo: nuevo });
        })
      );
      Swal.fire(
        "Listo",
        `Se ajustaron ${targetList.length} producto(s).`,
        "success"
      );
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e.message || "No se pudieron ajustar los precios.",
        "error"
      );
    }
  };

  const updatePrices = async (increase) => {
    const actionText = increase ? "aumentar" : "disminuir";
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Estás a punto de ${actionText} los precios en ${percentage}%.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, ¡hazlo!",
      cancelButtonText: "No, cancelar",
    });

    if (result.isConfirmed) {
      await handleBulkAdjustPrices(increase, percentage);
      onClose?.();
    } else {
      Swal.fire({
        title: "Cancelado",
        text: "Los cambios en los precios han sido cancelados.",
        icon: "info",
        confirmButtonText: "OK",
      });
      onClose?.();
    }
  };

  const handlePercentageChange = (e) => {
    let newValue = e.target.value;
    if (newValue === "0") newValue = "";
    setPercentage(newValue);
  };

  const decrementPercentage = () => {
    setPercentage((prev) => {
      const num = parseInt(prev, 10) || 0;
      return num > 1 ? num - 1 : 1;
    });
  };

  const incrementPercentage = () => {
    setPercentage((prev) => {
      const num = parseInt(prev, 10) || 0;
      return num + 1;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Ajustar Precios"
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 10,
          right: 0,
          background: "#EAEDF0",
          color: "#44566C",
          border: "none",
          borderRadius: "50%",
          width: 32,
          height: 32,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          lineHeight: 0,
          fontSize: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,.12)",
        }}
      >
        <i className="bi bi-x-lg"></i>
      </button>
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
        Ajustar Precios
      </h2>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 40,
          marginBottom: 40,
        }}
      >
        <button
          style={{
            backgroundColor: "#f0f0f0",
            border: "none",
            borderRadius: "50%",
            width: 40,
            height: 40,
            marginRight: 10,
            cursor: "pointer",
          }}
          onClick={decrementPercentage}
        >
          <i className="bi bi-dash" style={{ fontSize: "1.2rem" }}></i>
        </button>

        <div style={{ position: "relative", display: "inline-block" }}>
          <input
            type="number"
            value={percentage}
            onChange={handlePercentageChange}
            style={{
              width: 80,
              textAlign: "center",
              fontSize: "1rem",
              border: "1px solid #ccc",
              borderRadius: 5,
              padding: "6px",
              paddingRight: 24,
            }}
          />
          <span
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              color: "#999",
            }}
          >
            %
          </span>
        </div>

        <button
          style={{
            backgroundColor: "#f0f0f0",
            border: "none",
            borderRadius: "50%",
            width: 40,
            height: 40,
            marginLeft: 10,
            cursor: "pointer",
          }}
          onClick={incrementPercentage}
        >
          <i className="bi bi-plus" style={{ fontSize: "1.2rem" }}></i>
        </button>
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          style={{
            marginRight: 10,
            padding: "10px 20px",
            border: "none",
            borderRadius: 5,
            backgroundColor: "#28a745",
            color: "#fff",
            cursor: "pointer",
          }}
          onClick={() => updatePrices(true)}
        >
          <i className="bi bi-arrow-up-circle" style={{ marginRight: 5 }}></i>
          Aumentar
        </button>

        <button
          style={{
            padding: "10px 20px",
            border: "none",
            borderRadius: 5,
            backgroundColor: "#dc3545",
            color: "#fff",
            cursor: "pointer",
          }}
          onClick={() => updatePrices(false)}
        >
          <i className="bi bi-arrow-down-circle" style={{ marginRight: 5 }}></i>
          Disminuir
        </button>
      </div>
    </Modal>
  );
}
